"use client";

import React from "react";
import { CompanyOverview } from "@/features/companies/components/overview/CompanyOverview";
import { CompanyContacts } from "./company-contacts/CompanyContacts";
import { QuickCompanyCreate } from "./QuickCompanyCreate";
import { useCompanyHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import type { Contacts } from "@/src/gql/graphql";
import type { CompanyEntity } from "@/lib/types/company";

interface ContactCompanyTabProps {
  contact: Contacts;
  onRefresh?: () => void;
  onCompanyAdded?: (companyName: string) => void;
}

/**
 * ContactCompanyTab Component
 * 
 * Displays comprehensive company information and related contacts for a given contact.
 * Uses EntityContext pattern for reusability.
 * 
 * Layout:
 * 1. Company Overview (if company exists) - Rich overview with all company details
 * 2. Company Contacts - List of colleagues at the same company/domain
 * 3. Quick Company Create (if no company) - Form to create and link a company
 */
export const ContactCompanyTab: React.FC<ContactCompanyTabProps> = ({
  contact,
  onRefresh,
  onCompanyAdded,
}) => {
  // Get the company hooks
  const companyHooks = useCompanyHooks();
  
  // Use the GraphQL hook to fetch company by ID if available
  const { data: company, isLoading, error } = companyHooks.useSingle(
    contact.company_id || "",
    { enabled: !!contact.company_id }
  );

  // If no company is linked, show the quick create form
  if (!contact.company_name && !contact.company_id) {
    return (
      <QuickCompanyCreate
        contact={contact}
        onCompanyAdded={(companyName) => {
          onCompanyAdded?.(companyName);
          onRefresh?.();
        }}
      />
    );
  }

  // Show loading state while fetching company
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  // Show error state if company fetch failed but we have a company name
  if (error && contact.company_name) {
    return (
      <div className="space-y-6">
        <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-md dark:bg-amber-950/40 dark:border-amber-800">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Could not load full company details
            </p>
            <p className="text-sm text-amber-700 mt-1 dark:text-amber-300">
              Showing contacts for {contact.company_name}, but detailed company information is unavailable.
            </p>
          </div>
        </div>
        
        {/* Still show company contacts even if overview fails */}
        <CompanyContacts
          companyName={contact.company_name}
          currentContactId={contact.id}
          currentContact={contact}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Overview - Rich details about the company */}
      {company && (
        <div>
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4 dark:text-gray-100">
            Company Overview
          </h3>
          <CompanyOverview company={company} onRefresh={onRefresh} />
        </div>
      )}

      {/* Company Contacts - List of colleagues */}
      <div>
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4 dark:text-gray-100">
          {company ? "Team Members" : "Company Contacts"}
        </h3>
        <CompanyContacts
          companyName={contact.company_name || ""}
          currentContactId={contact.id}
          currentContact={contact}
        />
      </div>
    </div>
  );
};

